import * as echarts from 'echarts';
import type { BusinessLine } from '@monitoring-panel/types';

/**
 * Hex mirror of the Advia tokens (src/styles/advia-tokens.css) for ECharts,
 * which paints with resolved colours and can't read CSS variables. Same values
 * as the design system's advia-echarts-theme.js — never invent new ones.
 */
export const ADVIA_CHART_TOKENS = {
  graphite: '#292929',
  ivory: '#FCFDFD',
  gold: '#FAAD33',
  textPrimary: '#292929',
  textSecondary: '#7A7A7A',
  border: '#E3E3E3',
  axisPointer: '#CDCDCD',
  grid: 'rgba(41,41,41,.08)',
  neutralLight: '#A5A5A5',
  fontSans: '"Nunito Sans", system-ui, sans-serif',
  fontMono: '"Ubuntu Mono", ui-monospace, monospace',
} as const;

/** A dataviz series as its gradient stops (`--chart-N-from` / `--chart-N-to`). */
export interface SeriesColor {
  from: string;
  to: string;
}

export const CHART_SERIES = {
  amber: { from: '#FAAD33', to: '#FECB7B' },
  blue: { from: '#4B7AC1', to: '#04398A' },
  emerald: { from: '#10B981', to: '#059669' },
  sky: { from: '#8CA3B4', to: '#4A6377' },
  violet: { from: '#8B5CF6', to: '#6D28D9' },
  red: { from: '#F57171', to: '#9B1A1A' },
  pink: { from: '#F471B6', to: '#C21E77' },
  orange: { from: '#FAB351', to: '#C2410C' },
  cyan: { from: '#22D3EE', to: '#0891B2' },
  lime: { from: '#A3E635', to: '#65A30D' },
  fuchsia: { from: '#E879F9', to: '#A00CD3' },
  indigo: { from: '#5A5AA6', to: '#1F2455' },
  graphite: { from: '#4D4D4D', to: '#292929' },
} as const satisfies Record<string, SeriesColor>;

/** The 12 colour series in palette order (`--chart-1` … `--chart-12`). */
const CHART_PALETTE: readonly string[] = [
  CHART_SERIES.amber.from,
  CHART_SERIES.blue.from,
  CHART_SERIES.emerald.from,
  CHART_SERIES.sky.from,
  CHART_SERIES.violet.from,
  CHART_SERIES.red.from,
  CHART_SERIES.pink.from,
  CHART_SERIES.orange.from,
  CHART_SERIES.cyan.from,
  CHART_SERIES.lime.from,
  CHART_SERIES.fuchsia.from,
  CHART_SERIES.indigo.from,
];

/** Share of ivory mixed in for the design system's soft palette (`theme.soft`). */
const SOFT_MIX = 0.45;

function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  return [0, 2, 4].map((offset) => parseInt(value.substring(offset, offset + 2), 16)) as [
    number,
    number,
    number,
  ];
}

function rgbToHex(channels: number[]): string {
  return `#${channels
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

function softenHex(hex: string): string {
  const ivory = hexToRgb(ADVIA_CHART_TOKENS.ivory);
  return rgbToHex(hexToRgb(hex).map((channel, index) => channel + (ivory[index] - channel) * SOFT_MIX));
}

/** The same series a tone lower: context, forecasts, anything that sits behind. */
export function soft(series: SeriesColor): SeriesColor {
  return { from: softenHex(series.from), to: softenHex(series.to) };
}

function relativeLuminance(hex: string): number {
  const linear = (channel: number): number => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  const [red, green, blue] = hexToRgb(hex).map(linear);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/**
 * Solid used for legend dots, swatches and text: the darker stop, picked by
 * luminance (amber's `to` is the lighter one, so it keeps the brand gold).
 */
export function darkStop(series: SeriesColor): string {
  return relativeLuminance(series.to) < relativeLuminance(series.from) ? series.to : series.from;
}

/** The system's categorical order: amber first, graphite second, then the palette. */
const CATEGORICAL_ORDER: readonly SeriesColor[] = [
  CHART_SERIES.amber,
  CHART_SERIES.graphite,
  CHART_SERIES.blue,
  CHART_SERIES.emerald,
  CHART_SERIES.sky,
  CHART_SERIES.violet,
  CHART_SERIES.red,
  CHART_SERIES.pink,
  CHART_SERIES.orange,
  CHART_SERIES.cyan,
  CHART_SERIES.lime,
  CHART_SERIES.fuchsia,
  CHART_SERIES.indigo,
];

/** Series colour for the n-th category of a chart, wrapping past the palette. */
export function categoricalSeries(index: number): SeriesColor {
  return CATEGORICAL_ORDER[index % CATEGORICAL_ORDER.length];
}

/** Solids in the categorical order, for charts that take a plain colour list. */
export const CATEGORICAL_COLORS: readonly string[] = CATEGORICAL_ORDER.map((series) =>
  series === CHART_SERIES.graphite ? series.to : series.from
);

/**
 * The two greys of the system's sober scale (`escalaSobria`), for charts of 3
 * or 4 categories: amber, graphite, then neutral dark and neutral light.
 */
const NEUTRAL_SERIES = {
  dark: { from: '#4D4D4D', to: '#A5A5A5' },
  light: { from: '#A5A5A5', to: '#A5A5A5' },
} as const satisfies Record<string, SeriesColor>;

/** Solid fill of a series: graphite reads as the brand graphite, the rest as their base stop. */
export function solid(series: SeriesColor): string {
  return series === CHART_SERIES.graphite ? series.to : series.from;
}

/** Vertical light→dark gradient (`--chart-N-gradient`) as an ECharts colour. */
export function chartGradient(series: SeriesColor) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: series.from },
    { offset: 1, color: series.to },
  ]);
}

/** The same gradient as a CSS background, for swatches and progress bars. */
export function cssGradient(series: SeriesColor): string {
  return `linear-gradient(180deg, ${series.from}, ${series.to})`;
}

/**
 * Series colours of the revenue section, fixed per meaning so every page reads
 * the same. The revenue charts are combos, where the system puts the bars in
 * graphite and the line in amber, so the bar categories follow the categorical
 * order with amber left to the margin line: graphite, then blue, then emerald.
 * A forecast is its series a tone lower.
 */
export const BUSINESS_LINE_COLORS: Record<BusinessLine, SeriesColor> = {
  'display-video': CHART_SERIES.graphite,
  'chatgpt-ads': CHART_SERIES.blue,
  geo: CHART_SERIES.emerald,
};

export const REVENUE_COLORS = {
  served: CHART_SERIES.graphite,
  closing: CHART_SERIES.blue,
  forecast: soft(CHART_SERIES.graphite),
  margin: CHART_SERIES.amber,
} as const;

export const GEO_PLAN_COLORS = {
  mediaPlan: CHART_SERIES.amber,
  analytics: CHART_SERIES.graphite,
  mediaPlanForecast: soft(CHART_SERIES.amber),
  analyticsForecast: soft(CHART_SERIES.graphite),
} as const;

/** Four categories, so the sober scale: amber, graphite and the two greys. */
export const OPPORTUNITY_TYPE_COLORS = {
  branding: { actual: CHART_SERIES.amber, forecasted: soft(CHART_SERIES.amber) },
  performance: { actual: CHART_SERIES.graphite, forecasted: soft(CHART_SERIES.graphite) },
  youtube: { actual: NEUTRAL_SERIES.dark, forecasted: soft(NEUTRAL_SERIES.dark) },
  undefined: { actual: NEUTRAL_SERIES.light, forecasted: soft(NEUTRAL_SERIES.light) },
} as const;

/** Sober scale for three hierarchy levels: amber, graphite, light neutral. */
export const HIERARCHY_LEVEL_COLORS = {
  holding: CHART_SERIES.amber.from,
  agency: CHART_SERIES.graphite.to,
  account: ADVIA_CHART_TOKENS.neutralLight,
} as const;

/** Tooltip heading, styled with the design tokens (the tooltip lives in the DOM). */
export function tooltipTitle(text: string): string {
  return `<div style="font-weight:700;margin-bottom:8px;color:var(--text-primary)">${text}</div>`;
}

/** One `label: value` tooltip line; the figure goes in Ubuntu Mono. */
export function tooltipRow(label: string, value: string, marker = ''): string {
  return (
    `<div style="display:flex;align-items:center;gap:4px;margin:4px 0">${marker}` +
    `<span style="color:var(--text-secondary)">${label}:</span> ` +
    `<span style="font-family:var(--font-mono);font-weight:700;color:var(--text-primary)">${value}</span></div>`
  );
}

const valueAxisLabel = {
  color: ADVIA_CHART_TOKENS.textSecondary,
  fontFamily: ADVIA_CHART_TOKENS.fontMono,
  fontSize: 11,
};
const categoryAxisLabel = {
  color: ADVIA_CHART_TOKENS.textSecondary,
  fontFamily: ADVIA_CHART_TOKENS.fontSans,
  fontSize: 11,
};
const axisName = {
  color: ADVIA_CHART_TOKENS.textSecondary,
  fontFamily: ADVIA_CHART_TOKENS.fontSans,
  fontSize: 11,
};

const adviaTheme = {
  color: CHART_PALETTE,
  backgroundColor: 'transparent',
  textStyle: { fontFamily: ADVIA_CHART_TOKENS.fontSans, color: ADVIA_CHART_TOKENS.textPrimary },
  animationDuration: 750,
  animationEasing: 'cubicOut',
  animationDurationUpdate: 450,
  animationEasingUpdate: 'cubicInOut',
  categoryAxis: {
    axisLine: { lineStyle: { color: ADVIA_CHART_TOKENS.border } },
    axisTick: { show: false },
    axisLabel: categoryAxisLabel,
    splitLine: { show: false },
    nameTextStyle: axisName,
  },
  valueAxis: {
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: valueAxisLabel,
    splitLine: { lineStyle: { color: ADVIA_CHART_TOKENS.grid, width: 1 } },
    nameTextStyle: axisName,
  },
  legend: {
    textStyle: {
      color: ADVIA_CHART_TOKENS.textSecondary,
      fontFamily: ADVIA_CHART_TOKENS.fontSans,
      fontSize: 12,
    },
    icon: 'circle',
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 16,
  },
  tooltip: {
    backgroundColor: ADVIA_CHART_TOKENS.ivory,
    borderColor: ADVIA_CHART_TOKENS.border,
    borderWidth: 1,
    padding: 12,
    textStyle: {
      color: ADVIA_CHART_TOKENS.textPrimary,
      fontFamily: ADVIA_CHART_TOKENS.fontSans,
      fontSize: 12,
    },
    extraCssText: 'border-radius:8px;box-shadow:0 4px 16px rgba(41,41,41,.10);',
    axisPointer: {
      lineStyle: { color: ADVIA_CHART_TOKENS.axisPointer },
      crossStyle: { color: ADVIA_CHART_TOKENS.axisPointer },
    },
  },
  line: { smooth: true, symbol: 'circle', symbolSize: 6, lineStyle: { width: 1.5 } },
  pie: { itemStyle: { borderColor: ADVIA_CHART_TOKENS.ivory, borderWidth: 2 } },
};

/** Theme name to pass to `<ReactECharts theme={…}>`. */
export const ADVIA_ECHARTS_THEME = 'advia';

/** Dataviz renders as SVG, as the design system asks. */
export const ADVIA_CHART_OPTS = { renderer: 'svg' } as const;

echarts.registerTheme(ADVIA_ECHARTS_THEME, adviaTheme);
